import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { getAuthToken } from '../utils/safeStorage';
import {
  startPendingGiftAutoClaim,
  type GiftClaimResult,
} from '../utils/giftVoucherClaim';
import { giftClaimFollowUp } from '../pages/GiftVoucherPage/giftExperience';
import i18n from 'i18next';

export function notifyGiftClaimed(
  result: GiftClaimResult,
  navigate?: (path: string) => void,
  lang?: string | null
) {
  const followUp = giftClaimFollowUp(result, lang ?? i18n.language);
  toast.success(
    followUp.path ? `${followUp.message} · ${followUp.label}` : followUp.message,
    {
      autoClose: followUp.path ? 8000 : 5000,
      onClick: followUp.path && navigate ? () => navigate(followUp.path as string) : undefined,
    }
  );
}

/** Réclame un code saisi avant login, même si le widget de résa n’est plus à l’écran. */
export function usePendingGiftClaim(
  onClaimed?: (result: GiftClaimResult) => void | Promise<void>,
  enabled = true
) {
  const onClaimedRef = useRef(onClaimed);
  onClaimedRef.current = onClaimed;
  const { isAuthenticated } = useAuth();
  const { i18n: i18nHook } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || !enabled) return;
    const token = getAuthToken();
    startPendingGiftAutoClaim(token, {
      onSuccess: async result => {
        if (onClaimedRef.current) {
          await onClaimedRef.current(result);
          return;
        }
        notifyGiftClaimed(result, navigate, i18nHook.language);
      },
      onError: message => {
        toast.error(message);
      },
    });
  }, [isAuthenticated, enabled, navigate, i18nHook.language]);
}
